<script setup lang="ts">
type Icon = {
	icon: string
	title: string
}

const props = defineProps<{
	header?: string
	subHeader?: string
	paragraph?: string
	items?: string[]
	itemIcon?: string
	itemSize?: string
	buttonText: string
	buttonLink: string
	textColor?: string
	subText?: string
	bottomImage?: string
	mainImage: string
	icons?: Icon[]
	iconSize?: string
	iconDirection?: string
	background: string
}>()

const itemSize = props.itemSize || 's'

const itemIconClass = computed(() => {
	return itemSize === 's' ? 'max-w-4 w-4 max-h-4 md:max-w-6 md:w-6 md:max-h-6' : 'max-w-6 w-6 max-h-6 md:max-w-8 md:w-8 md:max-h-8'
})

const itemTextClass = computed(() => {
	return itemSize === 's' ? 'text-base md:text-lg' : 'text-lg md:text-xl'
})

const buttonTextClass = computed(() => {
	return itemSize === 's' ? '' : '!text-xl gap-2'
})

const iconsListClass = computed(() => {
	return props.iconDirection === 'row'
		? 'flex justify-center gap-8 flex-wrap lg:gap-16'
		: 'flex justify-center mt-16 gap-8 flex-wrap lg:gap-16'
})

const iconItemClass = computed(() => {
	return props.iconDirection === 'row'
		? 'flex items-center gap-4 flex-1 min-w-48'
		: 'flex flex-col items-center gap-2 flex-1 md:flex-none min-w-32'
})

const iconImageClass = computed(() => {
	switch (props.iconSize) {
		case 'm':
			return 'h-8'
		case 'l':
			return 'h-12'
		default:
			return 'h-6'
	}
})
</script>

<template>
	<header :style="{ background: background }" class="relative bg-cover">
		<slot name="background" />

		<div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 relative">
			<div class="flex flex-col gap-8 md:grid md:grid-cols-2">
				<div class="flex flex-col justify-center py-8">
					<slot name="header">
						<h1 v-if="header" class="text-xl md:text-5xl">{{ header }}</h1>
					</slot>
					<slot name="subHeader">
						<h2 v-if="subHeader" class="text-xl">{{ subHeader }}</h2>
					</slot>
					<span v-if="paragraph" :class="`${itemTextClass} ${textColor || 'text-color'} font-medium mb-0`">{{ paragraph }}</span>
					<div v-if="items" class="mt-4">
						<ul class="flex flex-col gap-2 pl-0">
							<li v-for="item in items" :key="item" class="flex items-center gap-2">
								<img
									:src="itemIcon"
									alt="Checkmark"
									:class="itemIconClass"
								/>
								<span :class="`${textColor || 'text-color'} ${itemTextClass} font-medium mb-0 flex-1`">{{ item }}</span>
							</li>
						</ul>
					</div>
					<div class="mt-4">
						<a :href="buttonLink" :class="`btn btn-primary flex items-center w-fit ${buttonTextClass}`">
							{{ buttonText }}
							<Icon name="ph:arrow-right-bold" class="size-6" aria-hidden="true" />
						</a>

						<span v-if="subText" :class="`${textColor || 'text-color'} ${itemTextClass} font-medium block mt-4`">{{ subText }}</span>
					</div>

					<img v-if="bottomImage" :src="bottomImage" alt="Bottom Image" class="mt-6 w-fit" />
				</div>
				<div class="flex justify-center items-center mb-12">
					<img :src="mainImage" alt="Main Image" />
				</div>
			</div>

			<!-- icons -->
			<div v-if="icons" :class="iconsListClass">
				<div v-for="icon in icons" :key="icon.icon" :class="iconItemClass">
					<img
						:src="icon.icon"
						alt="Icon"
						:class="iconImageClass"
					/>
					<span :class="`font-medium ${textColor || 'text-color'} mb-0 text-base ${iconDirection === 'row' ? 'text-start' : 'text-center'}`">{{ icon.title }}</span>
				</div>
			</div>
		</div>
	</header>
</template>

